import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router";

import { useTradeShows } from "@/api/queries";
import { useMe } from "@/auth/useMe";
import { Dialog } from "@/components/Dialog";
import { Pagination } from "@/components/Pagination";
import {
  EmptyState,
  ErrorState,
  Input,
  Page,
  Select,
  Spinner,
  Table,
} from "@/components/ui";
import { useAgents } from "@/leads/queries";
import { formatDate } from "@/lib/format";
import { CardForm } from "@/tarjetas/CardForm";
import type { Card } from "@/tarjetas/queries";
import { useCardImportChoices, useCardSummary, useCards } from "@/tarjetas/queries";

/**
 * *Tarjetas* — the digital card index: the business cards sellers collect at
 * trade shows, typed in by hand or brought in from a workbook.
 *
 * A seller sees their own cards and management sees everyone's, the legacy
 * rule. A row opens the card in the same form it was captured with.
 */
export function Tarjetas() {
  const { t } = useTranslation();
  const { data: me } = useMe();
  const isManager = me?.is_manager ?? false;
  const [params, setParams] = useSearchParams();
  const [editing, setEditing] = useState<Card | null>(null);

  const search = params.get("q") ?? "";
  const page = Math.max(1, Number(params.get("page") ?? 1) || 1);
  const tradeShow = params.get("expo") ?? "";
  const cardImport = params.get("importacion") ?? "";
  const agent = isManager ? (params.get("agente") ?? "") : "";

  // Typing stays local; the URL catches up once the seller pauses.
  const [draft, setDraft] = useState(search);
  useEffect(() => setDraft(search), [search]);
  useEffect(() => {
    if (draft === search) return;
    const timer = setTimeout(() => {
      setParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          if (draft) next.set("q", draft);
          else next.delete("q");
          next.delete("page");
          return next;
        },
        { replace: true },
      );
    }, 300);
    return () => clearTimeout(timer);
  }, [draft, search, setParams]);

  const { data, isPending, isError, isFetching, refetch } = useCards({
    search,
    page,
    tradeShow,
    cardImport,
    agent,
  });
  const summary = useCardSummary(agent);
  const tradeShows = useTradeShows();
  const agents = useAgents(isManager);
  const imports = useCardImportChoices(agent);

  /** Set one filter; `clear` names the ones it makes meaningless. */
  function update(key: string, value: string, clear: readonly string[] = []) {
    setParams((previous) => {
      const next = new URLSearchParams(previous);
      if (value) next.set(key, value);
      else next.delete(key);
      if (key !== "page") next.delete("page");
      for (const other of clear) next.delete(other);
      return next;
    });
  }

  const importOptions = imports.data ?? [];
  // An import reached by a link — from its report, say — that the picker does
  // not offer still has to read as the filter in force, not as "all of them".
  const importName = data?.results.find(
    (card) => card.card_import_name,
  )?.card_import_name;
  const strayImport =
    cardImport && !importOptions.some((option) => String(option.id) === cardImport);

  return (
    <Page
      title={t("cards.title")}
      subtitle={
        summary.data
          ? t("cards.count", { count: summary.data.total.toLocaleString("es-MX") })
          : undefined
      }
      actions={
        <>
          <Link to="/tarjetas/importar" className="btn btn-secondary">
            {t("cards.importExcel")}
          </Link>
          <Link to="/tarjetas/captura" className="btn btn-primary">
            {t("cards.capture")}
          </Link>
        </>
      }
    >
      {summary.data ? (
        <p className="mb-5 text-[13px] text-mute-600">
          {t("cards.summary", {
            manual: summary.data.manual.toLocaleString("es-MX"),
            imported: summary.data.imported.toLocaleString("es-MX"),
          })}
        </p>
      ) : null}

      <div className="mb-5 flex flex-wrap items-center gap-2.5">
        <Input
          type="search"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder={t("cards.searchPlaceholder")}
          aria-label={t("cards.search")}
          className="min-w-64 flex-1 sm:max-w-[449px] sm:flex-none"
        />
        <Select
          value={tradeShow}
          onChange={(event) => update("expo", event.target.value)}
          aria-label={t("cards.filterShow")}
          className="w-auto"
        >
          <option value="">{t("cards.allShows")}</option>
          {(tradeShows.data?.results ?? []).map((show) => (
            <option key={show.id} value={String(show.id)}>
              {show.name}
            </option>
          ))}
        </Select>
        {isManager ? (
          <Select
            value={agent}
            // An import is one agent's; kept across a change of agent it
            // would only empty the list.
            onChange={(event) => update("agente", event.target.value, ["importacion"])}
            aria-label={t("cards.filterAgent")}
            className="w-auto"
          >
            <option value="">{t("cards.allAgents")}</option>
            {(agents.data ?? []).map((option) => (
              <option key={option.id} value={String(option.id)}>
                {option.name || `#${option.id}`}
              </option>
            ))}
          </Select>
        ) : null}
        <Select
          value={cardImport}
          onChange={(event) => update("importacion", event.target.value)}
          aria-label={t("cards.filterImport")}
          className="w-auto max-w-80"
        >
          <option value="">{t("cards.allImports")}</option>
          {strayImport ? (
            <option value={cardImport}>{importName ?? `#${cardImport}`}</option>
          ) : null}
          {importOptions.map((option) => (
            <option key={option.id} value={String(option.id)}>
              {t("cards.importOption", {
                name:
                  // Management sees every agent's imports; the name says whose.
                  isManager && !agent && option.created_by_agent_name
                    ? `${option.name} · ${option.created_by_agent_name}`
                    : option.name,
                count: option.card_count.toLocaleString("es-MX"),
              })}
            </option>
          ))}
        </Select>
      </div>

      {isPending ? (
        <Spinner label={t("cards.loading")} />
      ) : isError ? (
        <ErrorState message={t("errors.cards")} onRetry={() => void refetch()} />
      ) : data.results.length === 0 ? (
        <EmptyState>{search ? t("cards.noMatches") : t("cards.empty")}</EmptyState>
      ) : (
        <div className="border-2 border-rule">
          <Table>
            <thead>
              <tr>
                <th>{t("cards.column.contact")}</th>
                <th>{t("cards.column.business")}</th>
                <th>{t("cards.column.reach")}</th>
                <th className="hidden lg:table-cell">{t("cards.column.city")}</th>
                <th className="hidden md:table-cell">{t("cards.column.show")}</th>
                <th className="hidden xl:table-cell">{t("cards.column.source")}</th>
                {isManager ? <th>{t("cards.column.agent")}</th> : null}
                <th className="hidden sm:table-cell">{t("cards.column.created")}</th>
              </tr>
            </thead>
            <tbody>
              {data.results.map((card) => (
                <tr
                  key={card.id}
                  className="cursor-pointer"
                  onClick={() => setEditing(card)}
                >
                  <td>
                    <button
                      type="button"
                      className="cursor-pointer text-left font-display font-extrabold"
                      onClick={(event) => {
                        event.stopPropagation();
                        setEditing(card);
                      }}
                    >
                      {card.name}
                    </button>
                    {card.job ? (
                      <div className="text-xs text-mute-600">{card.job}</div>
                    ) : null}
                  </td>
                  <td>{card.business}</td>
                  <td className="text-sm">
                    <div>{card.email}</div>
                    <div className="text-xs text-mute-600">
                      {[card.phone, card.mobile].filter(Boolean).join(" · ")}
                    </div>
                  </td>
                  <td className="hidden text-sm lg:table-cell">
                    {card.city_name ?? t("common.none")}
                  </td>
                  <td className="hidden text-sm md:table-cell">
                    {card.trade_show_name
                      ? `${card.trade_show_name}${card.edition ? ` ${card.edition}` : ""}`
                      : t("common.none")}
                  </td>
                  <td className="hidden text-sm xl:table-cell">
                    {card.card_import ? (
                      <Link
                        to={`/tarjetas/importaciones/${card.card_import}`}
                        onClick={(event) => event.stopPropagation()}
                      >
                        {card.card_import_name}
                      </Link>
                    ) : (
                      t("cards.manual")
                    )}
                  </td>
                  {isManager ? (
                    <td className="text-sm">
                      {card.current_agent_name ?? t("common.none")}
                    </td>
                  ) : null}
                  <td className="hidden text-sm whitespace-nowrap sm:table-cell">
                    {formatDate(card.created)}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          <Pagination
            page={page}
            count={data.count}
            shown={data.results.length}
            isFetching={isFetching}
            onChange={(next) => update("page", String(next))}
          />
        </div>
      )}

      <Dialog
        open={editing !== null}
        onClose={() => setEditing(null)}
        kicker={editing?.business}
        title={t("cards.editTitle")}
        width={980}
        closeOnBackdrop={false}
      >
        {editing ? (
          <CardForm
            key={editing.id}
            card={editing}
            onSaved={() => setEditing(null)}
            onCancel={() => setEditing(null)}
          />
        ) : null}
      </Dialog>
    </Page>
  );
}
